import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Globe, Check, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  INTAKE_CUSTOM_HOST_TXT_PREFIX,
  normalizeIntakeHostname,
  type IntakeCustomHostFields,
} from '../../lib/intakePublicUrl';
import { supabase } from '../../lib/supabase';
import { useBusiness } from '../../contexts/BusinessContext';

const DEFAULT_INTAKE_CNAME_TARGET =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_INTAKE_CUSTOM_HOST_CNAME_TARGET) ||
  'cname.vercel-dns.com';

type IntakeHostState = IntakeCustomHostFields & {
  intake_custom_host_verification_token?: string | null;
};

export interface BrandedIntakeHostSectionProps {
  businessId: string;
  onUpdated?: () => void;
}

/**
 * CRM app settings: optional custom hostname for public /intake/… links.
 * Parent should render only when Live Intake Forms is enabled.
 */
export default function BrandedIntakeHostSection({ businessId, onUpdated }: BrandedIntakeHostSectionProps) {
  const { updateBusiness } = useBusiness();
  const [intakeHost, setIntakeHost] = useState<IntakeHostState | null>(null);
  const [hostInput, setHostInput] = useState('');
  const [hostLoading, setHostLoading] = useState(false);
  const [hostSaving, setHostSaving] = useState(false);
  const [hostVerifying, setHostVerifying] = useState(false);
  const [hostRemoving, setHostRemoving] = useState(false);
  const step2Ref = useRef<HTMLDivElement>(null);
  const hadPendingTokenRef = useRef(false);

  const loadIntakeHost = useCallback(async () => {
    if (!businessId || !supabase) return;
    setHostLoading(true);
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('intake_custom_hostname, intake_custom_host_verified_at, intake_custom_host_verification_token')
        .eq('id', businessId)
        .maybeSingle();
      if (error) throw error;
      const row = data as IntakeHostState | null;
      setIntakeHost(row);
      setHostInput(row?.intake_custom_hostname || '');
    } catch (e) {
      console.error(e);
      toast.error('Failed to load intake domain settings');
    } finally {
      setHostLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void loadIntakeHost();
  }, [loadIntakeHost]);

  const fqdnForTxt = (hostname: string) => `${INTAKE_CUSTOM_HOST_TXT_PREFIX}.${normalizeIntakeHostname(hostname)}`;

  const copyText = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleSaveIntakeHostPending = async () => {
    const normalized = normalizeIntakeHostname(hostInput);
    if (!normalized) {
      toast.error('Enter a hostname (e.g. forms.yourbusiness.com)');
      return;
    }
    setHostSaving(true);
    try {
      const token = crypto.randomUUID();
      await updateBusiness(businessId, {
        intake_custom_hostname: normalized,
        intake_custom_host_verification_token: token,
        intake_custom_host_verified_at: null,
      });
      await loadIntakeHost();
      onUpdated?.();
      toast.success('Next: add the TXT record (step 2), then click Verify DNS.');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setHostSaving(false);
    }
  };

  const handleVerifyIntakeHost = async () => {
    if (!supabase) return;
    setHostVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-intake-custom-host', {
        body: { businessId },
      });
      if (error) throw new Error(error.message);
      if (data?.ok) {
        toast.success('Domain verified. Intake links will use your hostname.');
        if (data.vercelProvisioning === 'skipped' && data.vercelDetail) {
          toast(data.vercelDetail, { icon: 'ℹ️', duration: 10000 });
        }
        await loadIntakeHost();
        onUpdated?.();
      } else {
        toast.error(data?.message || 'Verification failed');
      }
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Verification failed');
    } finally {
      setHostVerifying(false);
    }
  };

  const handleStartOverPending = async () => {
    if (
      !window.confirm(
        'Discard this setup? DNS records you added can stay or be removed; you can enter the hostname again from step 1.',
      )
    ) {
      return;
    }
    setHostRemoving(true);
    try {
      await updateBusiness(businessId, {
        intake_custom_hostname: null,
        intake_custom_host_verified_at: null,
        intake_custom_host_verification_token: null,
      });
      setHostInput('');
      await loadIntakeHost();
      onUpdated?.();
      toast.success('Setup cleared — enter your hostname again when ready.');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not reset');
    } finally {
      setHostRemoving(false);
    }
  };

  const handleRemoveIntakeHost = async () => {
    if (!window.confirm('Remove your custom intake domain? Links will go back to the default app URL.')) return;
    setHostRemoving(true);
    try {
      await updateBusiness(businessId, {
        intake_custom_hostname: null,
        intake_custom_host_verified_at: null,
        intake_custom_host_verification_token: null,
      });
      setHostInput('');
      await loadIntakeHost();
      onUpdated?.();
      toast.success('Custom intake domain removed');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not remove');
    } finally {
      setHostRemoving(false);
    }
  };

  const pendingToken = intakeHost?.intake_custom_host_verification_token;
  const verified = Boolean(intakeHost?.intake_custom_host_verified_at);
  /** Saved hostname when pending — verification uses DB row, not the input box. */
  const hostnameForDns = pendingToken
    ? normalizeIntakeHostname(String(intakeHost?.intake_custom_hostname ?? ''))
    : normalizeIntakeHostname(hostInput);
  const txtFqdn = hostnameForDns ? fqdnForTxt(hostnameForDns) : '';
  const txtValue = pendingToken ? `nexus-intake-verify=${pendingToken}` : '';
  const showStep2 = Boolean(pendingToken) && !verified;

  useEffect(() => {
    const nowPending = Boolean(pendingToken) && !verified;
    if (nowPending && !hadPendingTokenRef.current && step2Ref.current) {
      step2Ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    hadPendingTokenRef.current = nowPending;
  }, [pendingToken, verified]);

  return (
    <div className="border-t border-gray-100 px-4 py-4 space-y-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
          <Globe className="h-4 w-4 text-violet-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900">Branded intake link (optional)</h3>
          <p className="text-xs text-gray-500 mt-1">
            Use your own subdomain for intake URLs. After DNS is set up, SMS and email links use{' '}
            <span className="font-mono text-gray-700">https://your-subdomain…/intake/…</span> instead of the default app
            domain.
          </p>
        </div>
      </div>

      {hostLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading domain settings…
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Intake hostname</label>
            <input
              type="text"
              value={hostInput}
              onChange={(e) => setHostInput(e.target.value)}
              placeholder="forms.yourbusiness.com"
              disabled={verified || Boolean(pendingToken)}
              title={
                pendingToken && !verified
                  ? 'Remove the domain or finish verification before changing the hostname'
                  : undefined
              }
              className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm disabled:bg-gray-50 disabled:text-gray-600"
            />
            {verified && (
              <p className="text-xs text-emerald-700 mt-1.5 flex items-center gap-1">
                <Check className="h-3.5 w-3.5" />
                Verified{' '}
                {intakeHost?.intake_custom_host_verified_at
                  ? format(new Date(intakeHost.intake_custom_host_verified_at), 'MMM d, yyyy h:mm a')
                  : ''}
              </p>
            )}
          </div>

          {!verified && (
            <>
              {!showStep2 && (
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3.5 text-xs text-gray-700 space-y-3">
                  <p className="font-semibold text-gray-900">Step 1 — Point your subdomain here</p>
                  <p>
                    At your DNS host (where you manage <span className="font-medium">{hostnameForDns || 'your domain'}</span>
                    ), add a <strong>CNAME</strong> whose <em>name</em> is usually the first part of the hostname above
                    (example: <code className="bg-white px-1 rounded">forms</code> for{' '}
                    <code className="bg-white px-1 rounded">forms.yourbusiness.com</code>) and whose <em>target</em> is:
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-[11px] bg-white border border-gray-200 rounded-lg px-2 py-1 break-all">
                      {DEFAULT_INTAKE_CNAME_TARGET}
                    </code>
                    <button
                      type="button"
                      onClick={() => void copyText('CNAME target', String(DEFAULT_INTAKE_CNAME_TARGET))}
                      className="inline-flex items-center gap-1 text-violet-600 font-semibold"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </button>
                  </div>
                  <p className="text-gray-600">DNS can take a few minutes to update everywhere.</p>
                  <button
                    type="button"
                    disabled={hostSaving}
                    onClick={() => void handleSaveIntakeHostPending()}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40"
                  >
                    {hostSaving ? 'Saving…' : 'Save & continue to step 2'}
                  </button>
                </div>
              )}

              {showStep2 && (
                <div ref={step2Ref} className="space-y-4">
                  <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5 text-[11px] text-gray-600">
                    <span className="font-semibold text-gray-800">Step 1</span> — Keep your{' '}
                    <strong>CNAME</strong> pointing to <code className="bg-white px-1 rounded">{DEFAULT_INTAKE_CNAME_TARGET}</code>
                    . You need both CNAME and TXT for verification and HTTPS.
                  </div>

                  <div className="rounded-xl bg-amber-50/80 border border-amber-100 p-3.5 text-xs text-amber-950 space-y-3">
                    <p className="font-semibold text-gray-900">Step 2 — Prove you own the domain</p>
                    <p>Add this <strong>TXT</strong> record at the same DNS host (new row in your DNS table):</p>
                    {txtFqdn && txtValue ? (
                      <div className="space-y-2">
                        <div>
                          <p className="text-[10px] font-medium uppercase tracking-wide text-amber-900/70 mb-1">Name / host</p>
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <span className="text-[11px] font-mono break-all leading-relaxed">{txtFqdn}</span>
                            <button
                              type="button"
                              onClick={() => void copyText('TXT name', txtFqdn)}
                              className="text-amber-900 font-semibold shrink-0 inline-flex items-center gap-1"
                            >
                              <Copy className="h-3.5 w-3.5" /> Copy
                            </button>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] font-medium uppercase tracking-wide text-amber-900/70 mb-1">Value</p>
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <span className="text-[11px] font-mono break-all leading-relaxed">{txtValue}</span>
                            <button
                              type="button"
                              onClick={() => void copyText('TXT value', txtValue)}
                              className="text-amber-900 font-semibold shrink-0 inline-flex items-center gap-1"
                            >
                              <Copy className="h-3.5 w-3.5" /> Copy
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      disabled={hostVerifying}
                      onClick={() => void handleVerifyIntakeHost()}
                      className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40"
                    >
                      {hostVerifying ? 'Checking DNS…' : 'Verify DNS'}
                    </button>
                    <button
                      type="button"
                      disabled={hostRemoving || hostVerifying}
                      onClick={() => void handleStartOverPending()}
                      className="block text-[11px] font-medium text-gray-500 hover:text-gray-800 underline-offset-2 hover:underline"
                    >
                      Start over (change hostname)
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {verified && intakeHost?.intake_custom_hostname && (
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                disabled={hostRemoving}
                onClick={() => void handleRemoveIntakeHost()}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-red-700 bg-red-50 border border-red-100 hover:bg-red-100 disabled:opacity-40"
              >
                {hostRemoving ? 'Removing…' : 'Remove custom domain'}
              </button>
            </div>
          )}

          {!verified && (
            <p className="text-[11px] text-gray-500">
              If people <em>sign in</em> on this subdomain, add it under Supabase → Authentication → URL configuration
              (redirect allowlist).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
